import { useCallback, useEffect, useState } from "react";

import { Button, TextField } from "@shapr3d/frontend-lib";
import type { ChangeEvent, FormEvent } from "react";

import useTranslation from "../../hooks/useTranslation";
import validatePassword from "../../lib/PasswordValidator";
import Heading from "../Heading/Heading";
import { ValidatedPasswordInput } from "../ValidatedPasswordInput/ValidatedPasswordInput";

import style from "./PasswordResetInput.module.css";

interface PasswordResetInputProps {
  onSubmit: (password: string) => Promise<void>;

  inProgress: boolean;
  errorMessage?: string;
}

const PasswordResetInput = (props: PasswordResetInputProps) => {
  const { t, m } = useTranslation();
  const [displayedError, setDisplayedError] = useState(props.errorMessage);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (props.errorMessage) {
      setDisplayedError(props.errorMessage);
    }
  }, [props.errorMessage]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (password.length > 72) {
        return setDisplayedError("");
      }

      if (!password || password !== confirmPassword) {
        setDisplayedError(t(m.ErrorMessage.NewPasswordsDontMatch));
        setPassword("");
        setConfirmPassword("");
        return;
      }

      if (!validatePassword(password).valid) {
        return setDisplayedError(t(m.ErrorMessage.WeakPassword));
      }

      await props.onSubmit(password);
    },
    [
      password,
      confirmPassword,
      m.ErrorMessage.NewPasswordsDontMatch,
      t,
      props,
      m.ErrorMessage.WeakPassword,
    ]
  );

  const handleInputFocus = useCallback(() => {
    if (displayedError) {
      setDisplayedError("");
    }
  }, [displayedError]);

  const handlePasswordChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  }, []);

  const handleConfirmPasswordChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value);
  }, []);

  return (
    <div className={style.passwordResetInput}>
      <div className={style.modalContainer}>
        <Heading type="h1">{t(m.ResetPassword.InputPageTitle)}</Heading>
        <form onSubmit={handleSubmit} className={style.modalForm}>
          <ValidatedPasswordInput
            type="password"
            placeholder={t(m.General.Password)}
            value={password}
            onChange={handlePasswordChange}
            onFocus={handleInputFocus}
            error={Boolean(displayedError)}
            disabled={props.inProgress}
            autoFocus
          />
          <TextField
            type="password"
            placeholder={t(m.General.ReEnterPassword)}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            onFocus={handleInputFocus}
            error={Boolean(displayedError)}
            disabled={props.inProgress}
          />
          {displayedError && <span className={style.error}>{displayedError}</span>}
          <Button type="submit" disabled={props.inProgress}>
            {t(m.General.Save)}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PasswordResetInput;
